import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder } from '@angular/forms';

import { Authenticator, HttpStatusResult, TokenSettings } from '@jct/core';
import { TimeSpan } from '@jct/core/system';

import {
  AccountService,
  ProfileService,
  AuthenticatorStates,
  IdentifyMethod,
  RegistrationNavigator,
} from '@jct/api';

import { StateFactory } from '@jct/ui';

@Component({
  selector: 'verify-form.page',
  templateUrl: './verify-form.html',
})
export class VerifyForm implements OnInit, OnDestroy {
  private _timer: any;
  private _redirect: string = null;

  constructor(
    private sf: StateFactory,
    private fb: UntypedFormBuilder,
    private accountService: AccountService,
    private authenticator: Authenticator,
    private profileService: ProfileService,
    private registrationNavigator: RegistrationNavigator,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  loadState = this.sf.create();
  verifyState = this.sf.create();

  identityKey: string = null;
  identityMethod: IdentifyMethod = IdentifyMethod.IdNumber;

  form = this.fb.group({
    token: [''],
    password: [''],
  });

  async ngOnInit() {
    // Mark ready immediately so the step paints without a blank flash;
    // params arrive synchronously on the first queryParams emission.
    await this.loadState.completed();

    this.route.queryParams.subscribe(async params => {
      this._redirect = params['redirect'] || null;
      this.identityKey = params['key'] || null;
      this.identityMethod = params['method'] || IdentifyMethod.IdNumber;

      if (!params['token']) {
        this.reset();
      } else {
        this.form.patchValue({
          token: params.token,
        });

        const tokenLifeTime = TimeSpan.parse(params.tokenLifeTime);

        this._timer = setTimeout(() => {
          this.reset();
        }, tokenLifeTime.totalSeconds * 1000);
      }
    });
  }

  ngOnDestroy() {
    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }
  }

  async reset() {
    await this.loadState.quit();
    await this.router.navigate(['/start/identify']);
  }

  async verify() {
    if (!this.verifyState.isInProcess) {
      this.form.disable();
      await this.verifyState.inProcess();

      const result = await this.accountService.verify(this.form.value);

      if (result instanceof HttpStatusResult) {
        this.form.enable();
        this.verifyState.failed(result);
        return;
      }

      if (result.state === AuthenticatorStates.LoggedIn) {
        this.authenticator.authenticate(<TokenSettings>result);
        const loadProfileResult = await this.profileService.loadRegistrationProfile();

        if (loadProfileResult instanceof HttpStatusResult) {
          this.form.enable();
          this.verifyState.failed(loadProfileResult);
          return;
        }

        await this.verifyState.completed();

        if (this._redirect && this.registrationNavigator.canNavigateToUrl(this._redirect)) {
          await this.router.navigateByUrl(this._redirect);
        } else {
          await this.registrationNavigator.navigateToCurrentStage();
        }
      }
    }
  }
}
